import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Space,
  Typography,
  message,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  InputNumber,
  Select,
  Switch,
  Input,
  Table,
  Empty,
  Tabs,
  Divider,
  Radio
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  BgColorsOutlined,
  MobileOutlined,
  PlayCircleOutlined,
  FontSizeOutlined,
  HistoryOutlined,
  SettingOutlined,
  RadarChartOutlined
} from '@ant-design/icons';
import { useSimpleStore } from '../stores/simpleStore';
import { PageLayout } from './common';
import { getApiUrl } from '../config';
import { apiClient } from '../services/unifiedApi';

// 타입 정의
import type { AppConfig, MediaFile } from './AppConfig/types';
import { backgroundTypeOptions, getVideoFiles } from './AppConfig/utils';
import BackgroundValueInput from './AppConfig/BackgroundValueInput';
import VideoSelect from './AppConfig/VideoSelect';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// 변경 이력 인터페이스
interface RevisionHistory {
  id: number;
  company_id?: number;
  user_name: string;
  change_summary: string;
  changed_count: number;
  revision_hash: string;
  created_at: string;
  created_date: string;
  ago: string;
}

const AppConfigManagement: React.FC = React.memo(() => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  // 변경 이력 상태
  const [revisionHistory, setRevisionHistory] = useState<RevisionHistory[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);

  const { user, selectedCompanyId, selectedCompanyName } = useSimpleStore();

  // 안전한 API 호출 함수
  const callApi = useCallback(async (apiCall: () => Promise<any>) => {
    try {
      return await apiCall();
    } catch (error) {
      console.error('API 호출 실패:', error);
      throw error;
    }
  }, []);


  // API 호출 함수들
  const loadMediaFiles = useCallback(async () => {
    if (!selectedCompanyId && user?.role !== 'admin') return;

    setLoadingMedia(true);
    try {
      let url = '/api/media/list?include_system=true&limit=1000';
      if (selectedCompanyId && selectedCompanyId !== 0) {
        url += `&company_id=${selectedCompanyId}`;
      }

      const result = await apiClient.get(url);

      if (result.success && result.data) {
        const filteredMedia = result.data.filter((file: any) => {
          const ext = file.filename.toLowerCase();
          return ext.endsWith('.mp4') || ext.endsWith('.mov') || ext.endsWith('.avi') ||
                 ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') ||
                 ext.endsWith('.gif') || ext.endsWith('.webp');
        });
        setMediaFiles(filteredMedia);
        console.log('📁 미디어 파일 로드됨:', filteredMedia.length + '개');
      }
    } catch (error) {
      console.error('미디어 파일 로드 실패:', error);
    } finally {
      setLoadingMedia(false);
    }
  }, [selectedCompanyId, user?.role, callApi]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/app-config';
      if (selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }

      const result = await apiClient.get(url);

      if (result.success && result.data?.config) {
        const configData = result.data.config;
        console.log('📥 서버에서 로드된 설정:', configData);

        setConfig(configData);

        // Form에 기본값 설정 (null/undefined 값 처리)
        const formValues = { ...configData };

        // 기본값이 없는 경우 합리적인 기본값 설정
        if (!formValues.BUS_STOP_DISPLAY_DURATION) formValues.BUS_STOP_DISPLAY_DURATION = 8;
        if (!formValues.NEWS_DISPLAY_DURATION) formValues.NEWS_DISPLAY_DURATION = 6;
        if (!formValues.WEATHER_DISPLAY_DURATION) formValues.WEATHER_DISPLAY_DURATION = 5;
        if (!formValues.BUS_STOP_EVENT_INTERVAL) formValues.BUS_STOP_EVENT_INTERVAL = 45;
        if (!formValues.ADS_BEFORE_NEWS_WEATHER) formValues.ADS_BEFORE_NEWS_WEATHER = 4;

        // 배경 설정 기본값
        if (!formValues.NEWS_BACKGROUND_TYPE) formValues.NEWS_BACKGROUND_TYPE = 'color';
        if (!formValues.NEWS_BACKGROUND_VALUE) formValues.NEWS_BACKGROUND_VALUE = '#ffffff';
        if (!formValues.WEATHER_BACKGROUND_TYPE) formValues.WEATHER_BACKGROUND_TYPE = 'color';
        if (!formValues.WEATHER_BACKGROUND_VALUE) formValues.WEATHER_BACKGROUND_VALUE = '#e6f7ff';
        if (!formValues.BUS_STOP_BACKGROUND_TYPE) formValues.BUS_STOP_BACKGROUND_TYPE = 'color';
        if (!formValues.BUS_STOP_BACKGROUND_VALUE) formValues.BUS_STOP_BACKGROUND_VALUE = '#f6ffed';

        // GPS 설정 기본값
        if (!formValues.GPS_SOURCE_TYPE) formValues.GPS_SOURCE_TYPE = 'BUILT_IN';
        if (formValues.FORCE_TIMER_MODE === undefined) formValues.FORCE_TIMER_MODE = false;
        if (formValues.DUAL_AD_MODE === undefined) formValues.DUAL_AD_MODE = false;
        if (formValues.SHOW_GPS_DEBUG_INFO === undefined) formValues.SHOW_GPS_DEBUG_INFO = false;
        if (formValues.UART_GPS_AUTO_SCAN === undefined) formValues.UART_GPS_AUTO_SCAN = true;
        if (!formValues.UART_GPS_PORT_PATH) formValues.UART_GPS_PORT_PATH = '';
        if (!formValues.UART_GPS_BAUD_RATE) formValues.UART_GPS_BAUD_RATE = 9600;
        if (!formValues.UART_GPS_RETRY_COUNT) formValues.UART_GPS_RETRY_COUNT = 3;
        if (!formValues.UART_GPS_CONNECTION_TIMEOUT) formValues.UART_GPS_CONNECTION_TIMEOUT = 10000;

        // company 경로가 포함된 파일 값들을 Form 표시용으로 변환
        const formDisplayValues = { ...formValues };
        const mediaFields = [
          'NEWS_BACKGROUND_VALUE',
          'WEATHER_BACKGROUND_VALUE',
          'BUS_STOP_BACKGROUND_VALUE',
          'WELCOME_VIDEO',
          'GOODBYE_VIDEO'
        ];

        mediaFields.forEach(field => {
          const value = formDisplayValues[field];
          if (value && typeof value === 'string' && value.startsWith('company_')) {
            // company_숫자/ 부분을 제거하고 파일명만 추출
            const filename = value.split('/').pop();
            if (filename) {
              formDisplayValues[field] = filename;
              console.log(`📋 ${field} Form 표시값 변환: ${value} → ${filename} (숫자 ID 기반)`);
            }
          }
        });

        console.log('📥 Form에 설정될 최종값:', formDisplayValues);
        form.setFieldsValue(formDisplayValues);
      }
    } catch (error) {
      console.error('앱 설정 로드 실패:', error);
      message.error('앱 설정을 불러올 수 없습니다');
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, callApi, form]);

  // 새로운 고성능 API를 사용한 변경 이력 로드
  const loadRevisionHistory = useCallback(async () => {
    setLoadingRevisions(true);
    try {
      // 캐시 무효화를 위한 타임스탬프 추가
      const timestamp = Date.now();
      let url = `/api/app-config/history/simple?limit=10&offset=0&_t=${timestamp}`;

      // admin도 운수사를 선택한 경우 해당 운수사의 이력 조회
      if (selectedCompanyId) {
        url += `&company_id=${selectedCompanyId}`;
      }

      console.log('📋 변경 이력 조회 URL:', url);

      const result = await callApi(() =>
        fetch(getApiUrl(url), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        }).then(res => res.json())
      );

      if (result.success && result.data?.history) {
        setRevisionHistory(result.data.history);
      } else {
        setRevisionHistory([]);
      }
    } catch (error) {
      console.error('변경 이력 로드 실패:', error);
      setRevisionHistory([]);
    } finally {
      setLoadingRevisions(false);
    }
  }, [selectedCompanyId, user?.role, callApi]);

  const saveConfig = useCallback(async (forceMode: boolean = false) => {
    try {
      setSaving(true);

      // 강제 저장 모드가 아닌 경우에만 validation 수행
      if (!forceMode) {
        try {
          await form.validateFields();
          console.log('✅ Form validation 성공');
        } catch (validationError: any) {
          console.error('❌ Form validation 실패:', validationError);

          // 상세한 에러 정보 로깅
          if (validationError.errorFields && validationError.errorFields.length > 0) {
            console.log('🔍 validation 실패 필드들:');
            const failedFields: string[] = [];

            validationError.errorFields.forEach((field: any) => {
              const fieldName = Array.isArray(field.name) ? field.name.join('.') : field.name;
              const fieldErrors = Array.isArray(field.errors) ? field.errors.join(', ') : field.errors;
              const fieldValue = field.value;

              console.log(`  - 필드: ${fieldName}`);
              console.log(`  - 에러: ${fieldErrors}`);
              console.log(`  - 현재값: ${fieldValue} (타입: ${typeof fieldValue})`);
              console.log(`  - 값이 null/undefined?: ${fieldValue == null}`);

              failedFields.push(`${fieldName} (${fieldErrors})`);
            });

            // 사용자에게 상세한 에러 메시지와 강제 저장 옵션 표시
            message.error({
              content: (
                <div>
                  <div style={{ marginBottom: '8px' }}>다음 필드에 오류가 있습니다:</div>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '12px' }}>
                    {failedFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </ul>
                  <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fff7e6', borderRadius: '4px', fontSize: '12px' }}>
                    💡 <strong>강제 저장하려면</strong>: 개발자 콘솔에서 <code>window.forceSaveConfig()</code> 실행
                  </div>
                </div>
              ),
              duration: 10
            });

            // 전역 강제 저장 함수 등록
            (window as any).forceSaveConfig = () => {
              console.log('🚀 강제 저장 모드로 재시도...');
              saveConfig(true);
            };

            return;
          } else {
            message.error('입력값을 확인해주세요');
            return;
          }
        }
      } else {
        console.log('🚀 강제 저장 모드로 실행 중...');
        message.warning('강제 저장 모드로 설정을 저장합니다', 3);
      }

      const formValues = form.getFieldsValue();

      // 더 관대한 값 필터링 - 빈 문자열도 허용하고, false도 허용
      const values = Object.fromEntries(
        Object.entries(formValues).filter(([key, value]) => {
          // null과 undefined만 제외하고 모든 값 허용 (빈 문자열, false, 0 등 포함)
          return value !== null && value !== undefined;
        })
      );

      // GPS_MODE_ENABLED를 항상 true로 설정 (UI에서 제거되었지만 백엔드/앱 호환성 유지)
      values.GPS_MODE_ENABLED = true;

      // 미디어 파일 경로 후처리: 숫자 ID 기반으로 올바른 경로 생성
      const processedValues = { ...values };

      if (selectedCompanyId !== null) {
        const mediaFields = [
          'NEWS_BACKGROUND_VALUE',
          'WEATHER_BACKGROUND_VALUE',
          'BUS_STOP_BACKGROUND_VALUE',
          'WELCOME_VIDEO',
          'GOODBYE_VIDEO'
        ];

        mediaFields.forEach(field => {
          const value = processedValues[field];
          if (value && typeof value === 'string' && !value.startsWith('company_') && !value.startsWith('#')) {
            // 색상이 아니고, 이미 company_ 경로가 포함되지 않은 경우에만 경로 추가
            const fieldType = field.includes('BACKGROUND') ?
              processedValues[field.replace('_VALUE', '_TYPE')] : 'video';

            if (fieldType === 'image' || fieldType === 'gif' || fieldType === 'video' ||
                field === 'WELCOME_VIDEO' || field === 'GOODBYE_VIDEO') {
              // 숫자 ID 사용: selectedCompanyId를 직접 사용
              processedValues[field] = `company_${selectedCompanyId}/${value}`;
              console.log(`📁 ${field} 경로 변환: ${value} → ${processedValues[field]} (숫자 ID 사용)`);
            }
          }
        });
      }

      console.log('📤 저장할 설정값:', processedValues);
      console.log('📊 저장할 필드 수:', Object.keys(processedValues).length);

      let url = '/api/app-config';
      if (selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }

      const result = await callApi(() =>
        fetch(getApiUrl(url), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ config: processedValues })
        }).then(res => res.json())
      );

      console.log('📦 서버 응답:', result);

      if (result && result.success) {
        const savedCount = Object.keys(processedValues).length;
        message.success({
          content: forceMode
            ? `🚀 강제 저장 완료! ${savedCount}개 설정이 저장되었습니다`
            : `✅ 앱 설정이 저장되었습니다 (${savedCount}개 필드)`,
          duration: 5
        });

        console.log('✅ 저장 성공 - 데이터 새로고침 시작');
        // 데이터 새로고침
        await loadConfig();
        await loadRevisionHistory();
        console.log('✅ 데이터 새로고침 완료');
      } else {
        const errorMsg = result?.error || result?.message || '설정 저장에 실패했습니다';
        console.error('❌ 저장 실패:', errorMsg);
        message.error({
          content: `저장 실패: ${errorMsg}`,
          duration: 8
        });
      }
    } catch (error) {
      console.error('앱 설정 저장 실패:', error);
      message.error('앱 설정을 저장할 수 없습니다');
    } finally {
      setSaving(false);
    }
  }, [selectedCompanyId, callApi, form, loadConfig, loadRevisionHistory]);

  // 전체 새로고침 함수
  const refreshAll = useCallback(() => {
    loadConfig();
    loadMediaFiles();
    loadRevisionHistory();
  }, [loadConfig, loadMediaFiles, loadRevisionHistory]);

  // useEffect 훅들
  useEffect(() => {
    if (selectedCompanyId !== null || user?.role === 'admin') {
      loadConfig();
      loadMediaFiles();
      loadRevisionHistory();
    }
  }, [selectedCompanyId, user?.role, loadConfig, loadMediaFiles, loadRevisionHistory]);

  // 비디오 파일 목록 계산
  const videoFiles = getVideoFiles(mediaFiles);

  // Form 값을 감시하여 타입 변경 시 값 초기화
  const watchedValues = Form.useWatch([], form);

  // 단순한 Form 값 변경 핸들러
  const handleFormChange = useCallback((changed: any, all: any) => {
    // 배경 타입이 변경되면 배경 값을 초기화
    if (changed.NEWS_BACKGROUND_TYPE) {
      form.setFieldsValue({ NEWS_BACKGROUND_VALUE: undefined });
    }
    if (changed.WEATHER_BACKGROUND_TYPE) {
      form.setFieldsValue({ WEATHER_BACKGROUND_VALUE: undefined });
    }
    if (changed.BUS_STOP_BACKGROUND_TYPE) {
      form.setFieldsValue({ BUS_STOP_BACKGROUND_VALUE: undefined });
    }

    // GPS 소스 타입이 변경되면 UART 설정을 기본값으로 초기화
    if (changed.GPS_SOURCE_TYPE) {
      if (changed.GPS_SOURCE_TYPE === 'UART') {
        form.setFieldsValue({
          UART_GPS_AUTO_SCAN: true,
          UART_GPS_PORT_PATH: '',
          UART_GPS_BAUD_RATE: 9600,
          UART_GPS_RETRY_COUNT: 3,
          UART_GPS_CONNECTION_TIMEOUT: 10000
        });
      }
    }
  }, [form]);

  // 변경 이력 테이블 컬럼 정의
  const revisionColumns = [
    {
      title: '시간',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (timestamp: string, record: RevisionHistory) => (
        <div>
          <div>{new Date(timestamp).toLocaleDateString('ko-KR')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {new Date(timestamp).toLocaleTimeString('ko-KR')}
          </Text>
        </div>
      )
    },
    {
      title: '사용자',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 100
    },
    {
      title: '변경 요약',
      dataIndex: 'change_summary',
      key: 'change_summary',
      render: (summary: string, record: RevisionHistory) => (
        <div>
          <Text style={{ fontSize: '13px' }}>{summary}</Text>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              해시: {record.revision_hash} · {record.ago}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: '변경 개수',
      dataIndex: 'changed_count',
      key: 'changed_count',
      width: 100,
      render: (count: number) => (
        <Text strong style={{ color: '#1890ff' }}>{count}개</Text>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageLayout
      title="앱 설정 관리"
      subtitle={`설정 대상: ${selectedCompanyName || '글로벌 설정'}`}
      actions={[
        {
          key: 'refresh',
          label: '새로고침',
          icon: <ReloadOutlined />,
          onClick: refreshAll,
          disabled: loading
        },
        {
          key: 'save',
          label: '설정 저장',
          type: 'primary',
          icon: <SaveOutlined />,
          onClick: saveConfig,
          loading: saving
        }
      ]}
    >
      <Alert
        message="앱 설정 안내"
        description={
          <div>
            <div>설정 변경 후 저장하면 클라이언트 앱의 다음 업데이트 주기(최대 30분)에 반영됩니다.</div>
            {selectedCompanyId && (
              <div style={{ marginTop: 8, color: '#1890ff' }}>
                💡 배경 미디어 선택 시 "{selectedCompanyName}"의 업로드된 파일 중에서 선택할 수 있습니다.
              </div>
            )}
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Tabs defaultActiveKey="config" type="card">
        <TabPane
          key="config"
          tab={
            <Space>
              <SettingOutlined />
              앱 설정
            </Space>
          }
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{}}
            onValuesChange={handleFormChange}
          >
        {/* 화면 레이아웃 모드 */}
        <Card title="화면 레이아웃 모드" style={{ marginBottom: 24 }}>
          <Form.Item
            name="SCREEN_LAYOUT_MODE"
            label="화면 표시 방식"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value={1}>모드 1: 전체화면 오버레이 (기본) - 광고 전체화면 + 뉴스/날씨/정류장 오버레이</Radio>
              <Radio value={2}>모드 2: 좌우 분할 (50:50) - 좌측 광고 상시 재생 + 우측 정류장 패널 상시 표시</Radio>
            </Radio.Group>
          </Form.Item>
          <Alert
            message="분할 모드(모드 2) 안내"
            description="좌측에 광고 영상이 항상 재생되고, 우측에 버스 정류장 정보(날씨, 노선번호, 시간, 이번/다음 정류장)가 항상 표시됩니다. 뉴스/날씨/노선도 오버레이는 표시되지 않습니다."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
          <Form.Item
            name="BUS_STOP_ALWAYS_VISIBLE"
            label="버스 정류장 상시 표시"
            valuePropName="checked"
          >
            <Switch checkedChildren="상시 표시" unCheckedChildren="이벤트 시만" />
          </Form.Item>
          <Alert
            message="상시 표시 모드"
            description="활성화하면 버스 정류장 정보가 항상 화면에 표시됩니다. 광고 영상 위에 오버레이로 표시되며, 정류장이 변경될 때마다 자동 갱신됩니다."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
          <Form.Item
            name="DUAL_AD_MODE"
            label="2개 동시광고"
            valuePropName="checked"
            style={{ marginTop: 16 }}
          >
            <Switch checkedChildren="2개 동시" unCheckedChildren="1개씩" />
          </Form.Item>
          <Alert
            message="2개 동시광고 모드"
            description="활성화하면 광고 영상 재생 시 화면을 좌우로 2분할해 2개 영상을 동시에 재생합니다. 플레이리스트 순서대로(좌=1번, 우=2번) 재생하고, 먼저 끝난 쪽에 다음 순번을 이어 재생합니다. 두 영상 모두 음소거됩니다."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </Card>

        {/* 시간 설정 섹션 */}
        <Card
          title={
            <Space>
              <ClockCircleOutlined />
              <Title level={4} style={{ margin: 0 }}>시간 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                name="BUS_STOP_DISPLAY_DURATION"
                label="버스정류장 표시 시간"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '버스정류장 표시 시간을 입력해주세요 (1초 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={60}
                  addonAfter="초"
                  placeholder="8"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="NEWS_DISPLAY_DURATION"
                label="뉴스 표시 시간"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '뉴스 표시 시간을 입력해주세요 (1초 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={60}
                  addonAfter="초"
                  placeholder="6"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="WEATHER_DISPLAY_DURATION"
                label="날씨 표시 시간"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '날씨 표시 시간을 입력해주세요 (1초 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={60}
                  addonAfter="초"
                  placeholder="5"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                name="ROUTE_MAP_DISPLAY_DURATION"
                label="전체 노선도 표시 시간"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '전체 노선도 표시 시간을 입력해주세요 (1초 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={60}
                  addonAfter="초"
                  placeholder="10"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="NEWS_ENABLED"
                label="뉴스 활성화"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="WEATHER_ENABLED"
                label="날씨 활성화"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="ROUTE_MAP_ENABLED"
                label="전체 노선도 활성화"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="DISASTER_DISPLAY_ENABLED"
                label="재난정보 표출"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="BUS_STOP_EVENT_INTERVAL"
                label="버스정류장 이벤트 간격"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '버스정류장 이벤트 간격을 입력해주세요 (1초 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={300}
                  addonAfter="초"
                  placeholder="45"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ADS_BEFORE_NEWS_WEATHER"
                label="뉴스/날씨 전 광고 개수"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1,
                  message: '뉴스/날씨 전 광고 개수를 입력해주세요 (1개 이상)'
                }]}
              >
                <InputNumber
                  min={1}
                  max={20}
                  placeholder="4"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 배경 미디어 섹션 */}
        <Card
          title={
            <Space>
              <BgColorsOutlined />
              <Title level={4} style={{ margin: 0 }}>배경 미디어 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          {/* 뉴스 배경 */}
          <Card
            type="inner"
            title="뉴스 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="NEWS_BACKGROUND_TYPE"
                  label="뉴스 배경 타입"
                  rules={[{
                    required: false,
                    message: '뉴스 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="NEWS_BACKGROUND_VALUE"
                  label="뉴스 배경 값"
                  rules={[{
                    required: false,
                    message: '뉴스 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.NEWS_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="뉴스 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 날씨 배경 */}
          <Card
            type="inner"
            title="날씨 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="WEATHER_BACKGROUND_TYPE"
                  label="날씨 배경 타입"
                  rules={[{
                    required: false,
                    message: '날씨 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="WEATHER_BACKGROUND_VALUE"
                  label="날씨 배경 값"
                  rules={[{
                    required: false,
                    message: '날씨 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.WEATHER_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="날씨 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 정류장 텍스트 스타일 */}
          <Card
            type="inner"
            title="정류장 텍스트 스타일"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="CURRENT_STOP_TEXT_COLOR"
                  label="이번 정류장 색상"
                >
                  <Input type="color" style={{ width: '100%', height: 32 }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="NEXT_STOP_TEXT_COLOR"
                  label="다음 정류장 색상"
                >
                  <Input type="color" style={{ width: '100%', height: 32 }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="STOP_TEXT_OUTLINE_COLOR"
                  label="외곽선 색상"
                >
                  <Input type="color" style={{ width: '100%', height: 32 }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="STOP_TEXT_OUTLINE_WIDTH"
                  label="외곽선 두께 (px)"
                >
                  <InputNumber min={0} max={30} step={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 버스정류장 배경 */}
          <Card
            type="inner"
            title="버스정류장 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="BUS_STOP_BACKGROUND_TYPE"
                  label="버스정류장 배경 타입"
                  rules={[{
                    required: false,
                    message: '버스정류장 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="BUS_STOP_BACKGROUND_VALUE"
                  label="버스정류장 배경 값"
                  rules={[{
                    required: false,
                    message: '버스정류장 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.BUS_STOP_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="버스정류장 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 분할 패널 배경 */}
          <Card
            type="inner"
            title="분할 패널 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="SPLIT_PANEL_BACKGROUND_TYPE"
                  label="분할 패널 배경 타입"
                  rules={[{
                    required: false,
                    message: '분할 패널 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="SPLIT_PANEL_BACKGROUND_VALUE"
                  label="분할 패널 배경 값"
                  rules={[{
                    required: false,
                    message: '분할 패널 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.SPLIT_PANEL_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="분할 패널 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* HDMI 버스정류장 배경 */}
          <Card
            type="inner"
            title="HDMI 버스정류장 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="HDMI_BUS_STOP_BACKGROUND_TYPE"
                  label="HDMI 버스정류장 배경 타입"
                >
                  <Select placeholder="배경 타입 선택">
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="HDMI_BUS_STOP_BACKGROUND_VALUE"
                  label="HDMI 버스정류장 배경 값"
                >
                  <BackgroundValueInput
                    type={watchedValues?.HDMI_BUS_STOP_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="HDMI 버스정류장 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 우회안내 배경 */}
          <Card
            type="inner"
            title="우회안내 배경 설정"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="DETOUR_BACKGROUND_TYPE"
                  label="우회안내 배경 타입"
                  rules={[{
                    required: false,
                    message: '우회안내 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="DETOUR_BACKGROUND_VALUE"
                  label="우회안내 배경 값"
                  rules={[{
                    required: false,
                    message: '우회안내 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.DETOUR_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="우회안내 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* 전체 노선도 배경 */}
          <Card
            type="inner"
            title="전체 노선도 배경 설정"
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="ROUTE_MAP_BACKGROUND_TYPE"
                  label="전체 노선도 배경 타입"
                  rules={[{
                    required: false,
                    message: '전체 노선도 배경 타입을 선택해주세요'
                  }]}
                >
                  <Select
                    placeholder="배경 타입 선택"
                  >
                    {backgroundTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="ROUTE_MAP_BACKGROUND_VALUE"
                  label="전체 노선도 배경 값"
                  rules={[{
                    required: false,
                    message: '전체 노선도 배경 값을 입력해주세요'
                  }]}
                >
                  <BackgroundValueInput
                    type={watchedValues?.ROUTE_MAP_BACKGROUND_TYPE || 'color'}
                    mediaFiles={mediaFiles}
                    loading={loadingMedia}
                    placeholder="전체 노선도 배경 값 입력"
                    companyId={selectedCompanyId}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Card>

        {/* GPS 설정 섹션 */}
        <Card
          title={
            <Space>
              <RadarChartOutlined />
              <Title level={4} style={{ margin: 0 }}>GPS 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            message="GPS 모드가 기본으로 활성화되어 있습니다"
            description="특별한 경우에만 하단의 '타이머 모드 강제 사용'을 활성화하세요. GPS가 정상 작동하지 않을 때 고정 시간 간격으로 동작합니다."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="GPS_SOURCE_TYPE"
                label="GPS 소스 타입"
                rules={[{
                  required: true,
                  message: 'GPS 소스 타입을 선택해주세요'
                }]}
                tooltip="GPS 신호를 받을 하드웨어를 선택합니다"
              >
                <Select placeholder="GPS 소스 타입 선택">
                  <Option value="BUILT_IN">내장 GPS</Option>
                  <Option value="UART">UART GPS</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="GPS_DETECTION_RADIUS_METERS"
                label="GPS 감지 반경"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
                tooltip="정류장을 감지할 반경 거리입니다"
              >
                <InputNumber
                  min={10}
                  max={1000}
                  addonAfter="m"
                  placeholder="100"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* UART GPS 설정 - 조건부 렌더링 */}
          {watchedValues?.GPS_SOURCE_TYPE === 'UART' && (
            <Card
              type="inner"
              title="UART GPS 상세 설정"
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="UART_GPS_AUTO_SCAN"
                    label="UART 자동 검색"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="활성화"
                      unCheckedChildren="비활성화"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="UART_GPS_PORT_PATH"
                    label="UART 포트 경로"
                    help="자동 검색이 활성화된 경우 비워두세요 (예: /dev/ttyUSB0)"
                  >
                    <Input
                      placeholder="/dev/ttyUSB0"
                      disabled={watchedValues?.UART_GPS_AUTO_SCAN}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="UART_GPS_BAUD_RATE"
                    label="UART 보드레이트"
                    rules={[{
                      required: false,
                      type: 'number',
                      message: '보드레이트를 선택해주세요'
                    }]}
                  >
                    <Select placeholder="보드레이트 선택">
                      <Option value={9600}>9600</Option>
                      <Option value={19200}>19200</Option>
                      <Option value={38400}>38400</Option>
                      <Option value={57600}>57600</Option>
                      <Option value={115200}>115200</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="UART_GPS_RETRY_COUNT"
                    label="연결 재시도 횟수"
                    rules={[{
                      required: false,
                      type: 'number',
                      min: 0,
                      max: 10,
                      message: '재시도 횟수를 입력해주세요 (0-10회)'
                    }]}
                  >
                    <InputNumber
                      min={0}
                      max={10}
                      placeholder="3"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="UART_GPS_CONNECTION_TIMEOUT"
                    label="연결 타임아웃"
                    rules={[{
                      required: false,
                      type: 'number',
                      min: 1000,
                      max: 60000,
                      message: '타임아웃을 입력해주세요 (1-60초)'
                    }]}
                  >
                    <InputNumber
                      min={1000}
                      max={60000}
                      step={1000}
                      addonAfter="ms"
                      placeholder="10000"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          <Divider />

          <Row gutter={[24, 16]}>
            <Col span={12}>
              {/* GPS 디버그 정보 표시 */}
              <Form.Item
                name="SHOW_GPS_DEBUG_INFO"
                label="GPS 디버그 정보 표시"
                valuePropName="checked"
                tooltip="화면에 GPS 좌표, 정확도 등 상세 정보를 표시합니다"
              >
                <Switch
                  checkedChildren="표시"
                  unCheckedChildren="숨김"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {/* 타이머 모드 강제 사용 */}
              <Form.Item
                name="FORCE_TIMER_MODE"
                label="타이머 모드 강제 사용"
                valuePropName="checked"
                extra="GPS를 사용하지 않고 고정 시간 간격으로 동작합니다"
              >
                <Switch
                  checkedChildren="강제 사용"
                  unCheckedChildren="사용 안 함"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 시스템 동작 섹션 */}
        <Card
          title={
            <Space>
              <MobileOutlined />
              <Title level={4} style={{ margin: 0 }}>시스템 동작 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                name="DEBUG_MODE"
                label="디버그 모드"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="활성화"
                  unCheckedChildren="비활성화"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="SHOW_DEBUG_TEXT"
                label="디버그 텍스트 표시"
                valuePropName="checked"
                tooltip="화면에 디버그 정보(모드, GPS 상태 등)를 표시합니다"
              >
                <Switch
                  checkedChildren="표시"
                  unCheckedChildren="숨김"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="FULLSCREEN_MODE"
                label="전체화면 모드"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="활성화"
                  unCheckedChildren="비활성화"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="VIDEO_RETRY_COUNT"
                label="비디오 재시도 횟수"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 0,
                  message: '비디오 재시도 횟수를 입력해주세요 (0회 이상)'
                }]}
              >
                <InputNumber
                  min={0}
                  max={10}
                  placeholder="3"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 비디오 설정 섹션 */}
        <Card
          title={
            <Space>
              <PlayCircleOutlined />
              <Title level={4} style={{ margin: 0 }}>비디오 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="WELCOME_VIDEO"
                label="시작 비디오"
              >
                <VideoSelect
                  videoFiles={videoFiles}
                  loading={loadingMedia}
                  placeholder="시작 비디오 선택"
                  allowNoVideo={true}
                  companyId={selectedCompanyId}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="GOODBYE_VIDEO"
                label="종료 비디오"
              >
                <VideoSelect
                  videoFiles={videoFiles}
                  loading={loadingMedia}
                  placeholder="종료 비디오 선택"
                  allowNoVideo={true}
                  companyId={selectedCompanyId}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <Form.Item
                name="VIDEO_LOADING_TIMEOUT"
                label="비디오 로딩 타임아웃"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 1000,
                  message: '비디오 로딩 타임아웃을 입력해주세요 (1000ms 이상)'
                }]}
              >
                <InputNumber
                  min={1000}
                  max={30000}
                  addonAfter="ms"
                  placeholder="10000"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 폰트 설정 섹션 */}
        <Card
          title={
            <Space>
              <FontSizeOutlined />
              <Title level={4} style={{ margin: 0 }}>폰트 설정</Title>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                name="CURRENT_STOP_TEXT_SIZE"
                label="현재 정류장 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="24"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="NEXT_STOP_TEXT_SIZE"
                label="다음 정류장 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="20"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="NEWS_TITLE_TEXT_SIZE"
                label="뉴스 제목 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="18"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={8}>
              <Form.Item
                name="NEWS_CONTENT_TEXT_SIZE"
                label="뉴스 내용 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="14"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="WEATHER_TEMP_TEXT_SIZE"
                label="날씨 온도 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="32"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="WEATHER_DESC_TEXT_SIZE"
                label="날씨 설명 텍스트 크기"
                rules={[{
                  required: false,
                  type: 'number',
                  min: 10,
                  message: 'GPS 감지 반경을 입력해주세요 (10m 이상)'
                }]}
              >
                <InputNumber
                  min={10}
                  max={100}
                  addonAfter="sp"
                  placeholder="16"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
          </Form>
        </TabPane>

        <TabPane
          key="history"
          tab={
            <Space>
              <HistoryOutlined />
              변경 이력
            </Space>
          }
        >
          <Card
            title={
              <Space>
                <HistoryOutlined />
                <Title level={4} style={{ margin: 0 }}>변경 이력</Title>
              </Space>
            }
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={loadRevisionHistory}
                loading={loadingRevisions}
                size="small"
              >
                새로고침
              </Button>
            }
          >
            {loadingRevisions ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16 }}>변경 이력을 불러오는 중...</p>
              </div>
            ) : revisionHistory.length === 0 ? (
              <Empty
                description="변경 이력이 없습니다"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: 40 }}
              >
                <Button type="primary" onClick={loadRevisionHistory}>
                  새로고침
                </Button>
              </Empty>
            ) : (
              <Table
                columns={revisionColumns}
                dataSource={revisionHistory}
                rowKey="id"
                size="small"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showQuickJumper: true,
                  showTotal: (total) => `총 ${total}개 이력`
                }}
              />
            )}
          </Card>
        </TabPane>
      </Tabs>
    </PageLayout>
  );
});

AppConfigManagement.displayName = 'AppConfigManagement';

export default AppConfigManagement;